<template>
    <div class="overflow-hidden">
        <BaseRemoteList
            ref="compactScheduleList"
            class="compact-schedule-list"
            :session-key="sessionKey"
            :subtitle="$t('veranstaltungen')"
            :collection-class="CompactScheduleCollection"
            :headers="headers"
            :filters="filters"
            :extra-params="extraParams"
            :options="{ sortBy: ['sort_date', 'location_title'], sortDesc: [false, false], itemsPerPage: 250 }"
            :item-class="rowClass"
            cache-data
            hide-toolbar
            :new-btn="false"
            :height="height"
            :collapse="collapse"
            @loading="loading = $event"
        >
            <template #item.event_status="{ item }">
                <EventStatusRenderer :item="item" />
            </template>
            <template #item.title="{ item }">
                <ScheduleTitleRenderer :item="item" />
            </template>
            <template #item.sort_date="{ item }">
                <ScheduleDateRenderer :item="item" />
            </template>
            <template #item.ownActions="{ item }">
                <ActionButtons :item="item" @edit="onEdit" @delete="deleteSchedule" />
            </template>
            <template #after-pagination>
                <IconBtn
                    :icon-cls="collapse ? 'next' : 'prev'"
                    icon-color="grey"
                    :tooltip="collapse ? $t('show_table') : $t('hide_table')"
                    @click.stop="collapse = !collapse"
                />
            </template>
        </BaseRemoteList>
    </div>
</template>

<script>
import { CompactSchedule, CompactScheduleCollection } from '@/models/CompactSchedule';
import EventStatusRenderer from './CompactListItemRenderers/EventStatusRenderer.vue';
import sessionStorageMixin from '@/mixins/session-storage';
import { errToStr } from '@/helpers/tools';
import ScheduleTitleRenderer from './CompactListItemRenderers/ScheduleTitleRenderer.vue';
import ScheduleDateRenderer from './CompactListItemRenderers/ScheduleDateRenderer.vue';
import ActionButtons from './CompactListItemRenderers/ActionButtons.vue';
import IconBtn from '../IconBtn.vue';

export default {
    name: 'ScheduleCompactList',

    components: {
        EventStatusRenderer,
        ScheduleTitleRenderer,
        ScheduleDateRenderer,
        ActionButtons,
        IconBtn,
    },
    mixins: [sessionStorageMixin],
    props: {
        sessionKey: {
            type: String,
            required: true,
        },
        height: {
            type: [String, Number],
            default: null,
        },
        enhancedFilters: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        const data = {
            cacheBuster: new Date().getTime(),
            loading: false,
            CompactScheduleCollection,
            // columns differ by role: staff members have a different view:
            headers: [
                { width: 30, sortable: false, value: 'event_status' },
                { text: this.$t('date'), width: 90, sortable: false, value: 'sort_date', align: 'end' },
                { text: this.$t('titel'), width: 200, sortable: false, value: 'event_title' },
                { text: this.$t('event_location'), width: 180, sortable: false, value: 'locationTitleWithCity' },
                { value: 'ownActions', sortable: false, width: 80 },
            ],
            collapse: false,
        };

        return data;
    },

    computed: {
        filters() {
            const filters = [];
            return filters;
        },

        extraParams() {
            const extraParams = {};
            const enhancedFilters = {};

            for (const key in this.enhancedFilters) {
                const value = this.enhancedFilters[key] ?? undefined;
                if (value !== undefined) {
                    enhancedFilters[key] = value;
                }
            }
            extraParams.enhancedFilters = enhancedFilters;

            return extraParams;
        },
    },

    created() {
        this.CompactSchedule = CompactSchedule;
    },

    methods: {
        onEdit(compactSchedule) {
            this.openEntry(compactSchedule);
        },
        async deleteSchedule(schedule) {
            if (!schedule) {
                return;
            }
            if (confirm(this.$t('confirm_rec_delete'))) {
                try {
                    await schedule.delete();
                    this.$emit('deleted', schedule);
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                }
            }
        },

        openEntry(compactSchedule) {
            // On existing records, we route to the info-category panel directly:
            if (Number(this.$route.params?.id) !== Number(compactSchedule?.event_id)) {
                this.$router.push({
                    name: 'event-compact-edit',
                    params: { id: compactSchedule.event_id, backRoute: { route: this.$route } },
                });
            }
        },
        rowClass(compactSchedule) {
            if (Number(compactSchedule.event_id) === Number(this.$route.params?.id)) {
                return 'schedule-list-row edited-record';
            }
            return 'schedule-list-row';
        },

        async reload() {
            await this.$refs.compactScheduleList.reload();
        },
    },
};
</script>

<style lang="scss" scoped>
.compact-schedule-list {
    min-width: 64px;

    :deep(.v-data-table-header) {
        th {
            height: 24px;
        }
    }
    :deep(.schedule-list-row) {
        &.edited-record,
        &.edited-record:hover {
            background-color: var(--primary-color) !important;
            color: white !important;
            .v-icon {
                background-color: white !important;
            }
        }
        td {
            height: 24px;
        }
    }
    :deep(.v-data-footer__select) {
        margin: 0;
    }
    :deep(.pagination) {
        justify-content: center;
    }

    &[collapse] {
        width: 0;

        :deep(.v-toolbar__content .subtitle),
        :deep(.pagination),
        :deep(.v-data-table__wrapper) {
            display: none;
            width: 0;
        }
    }
}
</style>
